import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const WORK_MODE_LABELS = { onsite: "On-site", remote: "Remote", hybrid: "Hybrid" };
export const EMP_TYPE_LABELS = { full_time: "Full-time", part_time: "Part-time", internship: "Internship", contract: "Contract", volunteer: "Volunteer" };
const CATEGORIES = ["Engineering", "Product", "Design", "Data & AI", "Marketing", "Sales", "Finance", "Operations", "Human Resources", "Education", "Healthcare", "Legal", "Research", "Nonprofit", "Other"];

const empty = {
  title: "", company: "", location: "", work_mode: "onsite", employment_type: "full_time",
  category: "Engineering", description: "", skills: "", salary_min: "", salary_max: "",
  salary_currency: "INR", salary_period: "year", apply_type: "in_app", apply_email: "", apply_url: "", country: "",
};

export default function JobFormDialog({ open, onOpenChange, initial, onSaved }) {
  const editing = !!(initial && initial.id);
  const [f, setF] = useState(() => initial ? {
    ...empty, ...initial,
    skills: Array.isArray(initial.skills) ? initial.skills.join(", ") : (initial.skills || ""),
    salary_min: initial.salary_min ?? "", salary_max: initial.salary_max ?? "",
  } : empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.title.trim() || !f.company.trim() || !f.description.trim()) {
      toast.error("Title, company and description are required."); return;
    }
    setSaving(true);
    const payload = {
      ...f,
      skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean),
      salary_min: f.salary_min === "" ? null : Number(f.salary_min),
      salary_max: f.salary_max === "" ? null : Number(f.salary_max),
    };
    try {
      const { data } = editing
        ? await api.put(`/jobs/${initial.id}`, payload)
        : await api.post("/jobs", payload);
      toast.success(editing ? "Job updated" : "Job posted");
      onSaved?.(data);
      onOpenChange(false);
      if (!editing) setF(empty);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save job");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="job-form-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? "Edit job" : "Post a job"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Job title *</Label>
              <Input value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder="Senior Product Manager" data-testid="job-field-title" />
            </div>
            <div className="space-y-1.5">
              <Label>Company / Organization *</Label>
              <Input value={f.company} onChange={(e) => set("company")(e.target.value)} placeholder="Acme Inc." data-testid="job-field-company" />
            </div>
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Input value={f.location} onChange={(e) => set("location")(e.target.value)} placeholder="Bengaluru, India" data-testid="job-field-location" />
            </div>
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Input value={f.country} onChange={(e) => set("country")(e.target.value)} placeholder="India" data-testid="job-field-country" />
            </div>
            <div className="space-y-1.5">
              <Label>Work mode</Label>
              <Select value={f.work_mode} onValueChange={set("work_mode")}>
                <SelectTrigger data-testid="job-field-workmode"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(WORK_MODE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Employment type</Label>
              <Select value={f.employment_type} onValueChange={set("employment_type")}>
                <SelectTrigger data-testid="job-field-emptype"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(EMP_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={f.category} onValueChange={set("category")}>
                <SelectTrigger data-testid="job-field-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Skills (comma separated)</Label>
              <Input value={f.skills} onChange={(e) => set("skills")(e.target.value)} placeholder="React, Python, SQL" data-testid="job-field-skills" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Description *</Label>
            <Textarea value={f.description} onChange={(e) => set("description")(e.target.value)} rows={6} placeholder="Role, responsibilities, requirements…" data-testid="job-field-description" />
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label>Salary min</Label>
              <Input type="number" value={f.salary_min} onChange={(e) => set("salary_min")(e.target.value)} placeholder="Optional" data-testid="job-field-salmin" />
            </div>
            <div className="space-y-1.5">
              <Label>Salary max</Label>
              <Input type="number" value={f.salary_max} onChange={(e) => set("salary_max")(e.target.value)} placeholder="Optional" data-testid="job-field-salmax" />
            </div>
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Input value={f.salary_currency} onChange={(e) => set("salary_currency")(e.target.value)} data-testid="job-field-currency" />
            </div>
            <div className="space-y-1.5">
              <Label>Per</Label>
              <Select value={f.salary_period} onValueChange={set("salary_period")}>
                <SelectTrigger data-testid="job-field-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="year">Year</SelectItem>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="hour">Hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-xl bg-muted/50 p-4">
            <Label className="mb-2 block">How should people apply?</Label>
            <Select value={f.apply_type} onValueChange={set("apply_type")}>
              <SelectTrigger data-testid="job-field-applytype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="in_app">Apply within AlumniPulse</SelectItem>
                <SelectItem value="email">Apply by email</SelectItem>
                <SelectItem value="url">Apply on external website</SelectItem>
              </SelectContent>
            </Select>
            {f.apply_type === "email" && (
              <Input className="mt-3" value={f.apply_email} onChange={(e) => set("apply_email")(e.target.value)} placeholder="jobs@company.com" data-testid="job-field-applyemail" />
            )}
            {f.apply_type === "url" && (
              <Input className="mt-3" value={f.apply_url} onChange={(e) => set("apply_url")(e.target.value)} placeholder="https://company.com/careers/123" data-testid="job-field-applyurl" />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="job-form-submit">{saving ? "Saving…" : editing ? "Save changes" : "Post job"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
